import {Platform} from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'

const HISTORY_KEY = 'tellustv:watch-history:v1'
const MAX_ENTRIES = 200

export type WatchResumeEntry = {
  videoPageUri: string
  segmentIndex: number
  offsetMs: number
  positionMs: number
  updatedAt: number
  totalDurationMs?: number
  title?: string
  authorDid?: string
  thumbnail?: string
}

export type WatchHistoryState = {
  entries: Record<string, WatchResumeEntry>
  authorWatchMs: Record<string, number>
}

const listeners = new Set<() => void>()
let cache: WatchHistoryState | undefined
let cachePromise: Promise<WatchHistoryState> | undefined

function safeParse<T>(raw: string | null): T | undefined {
  if (!raw) return undefined
  try {
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

async function getItem<T>(key: string): Promise<T | undefined> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.localStorage) return undefined
    return safeParse<T>(window.localStorage.getItem(key))
  }
  const raw = await AsyncStorage.getItem(key)
  return safeParse<T>(raw)
}

async function setItem<T>(key: string, value: T): Promise<void> {
  const raw = JSON.stringify(value)
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.localStorage) return
    try {
      window.localStorage.setItem(key, raw)
    } catch {
      return
    }
    return
  }
  await AsyncStorage.setItem(key, raw)
}

function emit() {
  listeners.forEach(listener => listener())
}

function getDefaultState(): WatchHistoryState {
  return {entries: {}, authorWatchMs: {}}
}

export async function loadWatchHistory(): Promise<WatchHistoryState> {
  if (cache) return cache
  if (!cachePromise) {
    cachePromise = getItem<WatchHistoryState>(HISTORY_KEY).then(state => {
      cache = state || getDefaultState()
      return cache
    })
  }
  return cachePromise
}

export async function saveWatchHistory(
  state: WatchHistoryState,
): Promise<void> {
  cache = state
  await setItem(HISTORY_KEY, state)
  emit()
}

export function subscribeWatchHistory(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export async function recordWatchProgress({
  entry,
  deltaMs,
}: {
  entry: WatchResumeEntry
  deltaMs?: number
}): Promise<WatchHistoryState> {
  const state = await loadWatchHistory()
  const nextEntries = {
    ...state.entries,
    [entry.videoPageUri]: entry,
  }

  const authorWatchMs = {...state.authorWatchMs}
  if (entry.authorDid && deltaMs && deltaMs > 0) {
    authorWatchMs[entry.authorDid] =
      (authorWatchMs[entry.authorDid] || 0) + deltaMs
  }

  const pruned = pruneEntries(nextEntries)
  const nextState = {
    entries: pruned,
    authorWatchMs,
  }
  await saveWatchHistory(nextState)
  return nextState
}

export async function getResumeEntry(
  videoPageUri: string,
): Promise<WatchResumeEntry | undefined> {
  const state = await loadWatchHistory()
  return state.entries[videoPageUri]
}

export function getContinueWatchingEntries(
  state: WatchHistoryState,
): WatchResumeEntry[] {
  return Object.values(state.entries)
    .filter(entry => {
      if (!entry.totalDurationMs) return true
      return entry.positionMs < entry.totalDurationMs - 30_000
    })
    .sort((a, b) => b.updatedAt - a.updatedAt)
}

export function getAuthorWatchMs(
  state: WatchHistoryState,
  authorDid?: string,
): number {
  if (!authorDid) return 0
  return state.authorWatchMs[authorDid] || 0
}

function pruneEntries(entries: Record<string, WatchResumeEntry>) {
  const all = Object.values(entries)
  if (all.length <= MAX_ENTRIES) return entries
  const sorted = all.sort((a, b) => b.updatedAt - a.updatedAt)
  const keep = new Set(sorted.slice(0, MAX_ENTRIES).map(e => e.videoPageUri))
  const next: Record<string, WatchResumeEntry> = {}
  for (const uri of keep) {
    const entry = entries[uri]
    if (entry) next[uri] = entry
  }
  return next
}
